import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2, Edit3, Search, Package } from "lucide-react";
import { norm } from "@/lib/normalize";
import { useLookups } from "@/context/LookupContext";
import { formatINR } from "@/lib/currency";
import { useConfirm } from "@/components/ConfirmDialog";

const empty = { name: "", categoryId: "", brand: "", purchaseUnit: "", baseUnit: "", conversionFactor: 1, costPrice: 0, sellingPrice: 0, consumable: false };

export default function Products() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterConsumable, setFilterConsumable] = useState("ALL");
  const { lookups } = useLookups();
  const PURCHASE_UNITS = (lookups?.purchaseUnits || []).map((u) => u.name || u);
  const BASE_UNITS = (lookups?.baseUnits || []).map((u) => u.name || u);
  const CATEGORIES = lookups?.productCategories || [];
  const isAdmin = norm(user.roleName) === "admin" || norm(user.roleName) === "receptionist" || norm(user.roleName) === "manager";

  const load = async () => {
    try { setRows((await api.get("/products")).data); } catch { toast.error("Failed"); }
  };
  useEffect(() => { load(); }, []);

  const filteredRows = useMemo(() => {
    return (rows || []).filter((p) => {
      const name = (p.name || "").toLowerCase();
      const search = searchText.toLowerCase();
      if (search && !name.includes(search)) return false;
      if (filterCategory && filterCategory !== "all" && String(p.categoryId || "") !== filterCategory) return false;
      if (filterConsumable === "CONSUMABLE" && p.consumable !== true) return false;
      if (filterConsumable === "NON_CONSUMABLE" && p.consumable === true) return false;
      return true;
    });
  }, [rows, searchText, filterCategory, filterConsumable]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, categoryId: form.categoryId ? Number(form.categoryId) : null, costPrice: Number(form.costPrice), sellingPrice: Number(form.sellingPrice) };
      if (editId) await api.put(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Failed"); }
  };

  const confirm = useConfirm();
  const del = async (id) => {
    if (!(await confirm("Delete this product?"))) return;
    try { await api.delete(`/products/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8" data-testid="products-page">
      <div className="flex items-end justify-between gap-4">
        <div><div className="overline mb-2">Catalogue</div><h1 className="font-heading text-4xl text-white inline-flex items-center gap-3"><Package className="w-8 h-8 text-blue-500" /> Products</h1></div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button onClick={() => { setForm(empty); setEditId(null); }} data-testid="add-product-btn" className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Create Product</button>
            </DialogTrigger>
            <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-lg">
              <DialogHeader><DialogTitle className="font-heading text-2xl">{editId ? "Edit" : "New"} Product</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category</Label>
                    <Select value={form.categoryId} onValueChange={(val) => setForm({ ...form, categoryId: val })}>
                      <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => (
                          <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Purchase Unit</Label>
                    <Select value={form.purchaseUnit} onValueChange={(val) => setForm({ ...form, purchaseUnit: val })}>
                      <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                        <SelectValue placeholder="Select purchase unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {PURCHASE_UNITS.map((u) => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Base Unit</Label>
                    <Select value={form.baseUnit} onValueChange={(val) => setForm({ ...form, baseUnit: val })}>
                      <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-9">
                        <SelectValue placeholder="Select base unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {BASE_UNITS.map((u) => (
                          <SelectItem key={u} value={u}>{u}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div><Label>Conversion Factor</Label><Input type="number" step="0.01" value={form.conversionFactor} onChange={(e) => setForm({ ...form, conversionFactor: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Cost Price (₹)</Label><Input required type="number" step="0.01" value={form.costPrice} onChange={(e) => setForm({ ...form, costPrice: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                  <div><Label>Selling Price (₹)</Label><Input required type="number" step="0.01" value={form.sellingPrice} onChange={(e) => setForm({ ...form, sellingPrice: e.target.value })} className="bg-emerald-elev border-gold-subtle mt-1" /></div>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox id="consumable" checked={form.consumable} onCheckedChange={(v) => setForm({ ...form, consumable: !!v })} />
                  <Label htmlFor="consumable" className="text-sm cursor-pointer">Is Consumable</Label>
                </div>
                <button type="submit" className="gold-button w-full h-11 rounded-md">Save</button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-muted" />
          <Input
            placeholder="Search by name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-9 bg-emerald-surface border-gold-subtle text-white h-10"
            data-testid="products-search"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.filter((c) => !["consumables", "retail products"].includes((c.name || "").toLowerCase())).map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterConsumable} onValueChange={setFilterConsumable}>
          <SelectTrigger className="bg-emerald-surface border-gold-subtle text-white h-10" data-testid="products-consumable-filter">
            <SelectValue placeholder="All Products" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Products</SelectItem>
            <SelectItem value="CONSUMABLE">Consumables</SelectItem>
            <SelectItem value="NON_CONSUMABLE">Retail</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
              <TableHead className="th-gold">Name</TableHead>
              <TableHead className="th-gold">Category</TableHead>
              <TableHead className="th-gold">Brand</TableHead>
              <TableHead className="th-gold">Purchase Unit</TableHead>
              <TableHead className="th-gold text-right">Cost Price</TableHead>
              <TableHead className="th-gold text-right">Selling Price</TableHead>
              {isAdmin && <TableHead className="th-gold w-24"></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRows.map((p) => (
              <TableRow key={p.id} className="border-gold-subtle" data-testid={`product-row-${p.id}`}>
                <TableCell className="text-white">{p.name}</TableCell>
                <TableCell className="text-emerald-muted">{CATEGORIES.find((c) => c.id === p.categoryId)?.name || p.category || ""}</TableCell>
                <TableCell className="text-emerald-muted">{p.brand}</TableCell>
                <TableCell className="text-emerald-muted">{p.purchaseUnit}{p.baseUnit ? `(${p.baseUnit})` : ""}</TableCell>
                <TableCell className="text-right text-white">{formatINR(p.costPrice)}</TableCell>
                <TableCell className="text-right text-gold">{formatINR(p.sellingPrice)}</TableCell>
                {isAdmin && (
                  <TableCell className="text-right">
                    <button onClick={() => { setForm({ name: p.name, categoryId: p.categoryId ? String(p.categoryId) : "", brand: p.brand || "", purchaseUnit: p.purchaseUnit || "", baseUnit: p.baseUnit || "", conversionFactor: p.conversionFactor || 1, costPrice: p.costPrice, sellingPrice: p.sellingPrice, consumable: p.consumable || false }); setEditId(p.id); setOpen(true); }} className="text-emerald-muted hover:text-gold p-1"><Edit3 className="w-4 h-4" /></button>
                    <button onClick={() => del(p.id)} className="text-emerald-muted hover:text-red-300 p-1 ml-2" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
